import {
  ActivityState,
  AllocationStatus,
  RegistrationMode,
} from "@timeedit/registration-schemas";
import {
  CheckLinkedGroupsResult,
  exhaustiveMatchingGuard,
} from "@timeedit/registration-shared";
import { ButtonProps, Tooltip } from "@timeedit/ui-components";
import cn from "classnames";
import { ReactNode } from "react";
import "./allocationControls.scss";
import {
  DescriptionDisabled,
  Translations,
  WaitlistButtonProps,
} from "./types";
import { useDisabledWaitlistState } from "./hooks";
import { MultipleAllocations } from "./ButtonStates/MultipleAllocations";
import { NotAllocated } from "./ButtonStates/NotAllocated";
import { AllocatedToOther } from "./ButtonStates/AllocatedToOther";
import { AllocatedToThis } from "./ButtonStates/AllocatedToThis";

export interface AllocationControlsProps extends Omit<ButtonProps, "disabled"> {
  groupId: number;
  currentLoadingGroup: number;
  state: ActivityState;
  translations: Translations;
  onAllocate: () => void;
  onDeallocate: () => void;
  full?: boolean;
  clash?: boolean;
  blockStudentConflict?: boolean;
  allocationStatus?: AllocationStatus;
  mode?: RegistrationMode;
  open?: boolean;
  linkedValidation?: CheckLinkedGroupsResult;
  disabledState?: DescriptionDisabled;
  groupName: string;
  activityName: string;
  waitlistProps: {
    onJoinWaitlist: () => void;
    onLeaveWaitlist: () => void;
    isOnWaitlist: boolean;
    enableWaitlist: boolean;
    hasStudentsInWaitlist: boolean;
  };
}

/**
 *
 * @param translations - Translations for various button texts and messages
 * @param onAllocate - Callback when the register button is clicked
 * @param onDeallocate - Callback when the deallocate button is clicked
 * @param full - Whether the group is full
 * @param clash - Whether there is a scheduling clash with the group
 * @param blockStudentConflict - If we should allow a student to allocate through conflict
 * @param allocationStatus - Current allocation status of the user for this group
 * @param mode - Registration mode, who is using the controls
 * @param open - Whether the group is open for registration
 * @param groupId - ID of the group being allocated to
 * @param currentLoadingGroup - ID of the group currently loading (for showing loading states)
 * @param linkedValidation - Result of linked group validation
 * @param disabledState - Object describing if the button should be disabled and why (does not override logic if disabled state is set to false)
 * @param activityName - Name of the activity related to the current button
 * @param groupName - Name of the group related to the current button
 * @param waitlistProps.isOnWaitlist - Whether the user is currently on the waitlist in this group
 * @param waitlistProps.enableWaitlist - Whether waitlist feature is enabled
 * @param waitlistProps.onJoinWaitlist - Callback when the join waitlist button is clicked
 * @param waitlistProps.onLeaveWaitlist - Callback when the leave waitlist button is clicked
 * @param waitlistProps.hasStudentsInWaitlist - Whether there are any students in the waitlist for this group
 *
 */
export function AllocationControls({
  state,
  translations,
  onAllocate,
  onDeallocate,
  full = false,
  clash = false,
  blockStudentConflict = false,
  allocationStatus = "NOT_ALLOCATED",
  mode = "student",
  open = false,
  groupId,
  currentLoadingGroup,
  linkedValidation = { success: true, message: "" },
  disabledState,
  waitlistProps,
  activityName,
  groupName,
  ...buttonProps
}: AllocationControlsProps): React.JSX.Element {
  const {
    isOnWaitlist = false,
    enableWaitlist = false,
    onJoinWaitlist,
    onLeaveWaitlist,
    hasStudentsInWaitlist,
  } = waitlistProps;
  const { waitlistState, disabled, description } = useDisabledWaitlistState({
    enableWaitlist,
    full,
    allocationStatus,
    mode,
    open,
    blockStudentConflict,
    clash,
    linkedValidationSuccess: linkedValidation.success,
    isOnWaitlist,
    disabledState,
    translations,
    hasStudentsInWaitlist,
  });

  const loadingThis =
    currentLoadingGroup > 0 && currentLoadingGroup === groupId;
  const loadingOther =
    currentLoadingGroup > 0 && currentLoadingGroup !== groupId;

  return (
    <Tooltip title={mode === "teacher" ? linkedValidation.message : ""}>
      <div className={cn("allocation-container", buttonProps.className)}>
        {renderContent()}
      </div>
    </Tooltip>
  );

  function renderContent(): React.JSX.Element {
    switch (state) {
      case "ADJUST":
      case "UNKNOWN":
      case "PREFERENCE":
      case "INCONCLUSIVE":
      case "SELECT":
        return (
          <AllocationButton
            {...buttonProps}
            mode={mode}
            linkedIssue={mode === "teacher" && !linkedValidation.success}
            loading={loadingThis || buttonProps.loading}
            size="large"
            translations={translations}
            clash={clash}
            allocationStatus={allocationStatus}
            disabled={disabled || loadingOther}
            description={description}
            onAllocate={onAllocate}
            onDeallocate={onDeallocate}
            groupName={groupName}
            activityName={activityName}
            waitListProps={{
              waitlistState,
              isOnWaitlist,
              onJoinWaitlist,
              onLeaveWaitlist,
            }}
          />
        );
      default: {
        return exhaustiveMatchingGuard(state);
      }
    }
  }
}

interface AllocationButtonProps extends ButtonProps {
  translations: Translations;
  onAllocate: () => void;
  onDeallocate: () => void;
  clash: boolean;
  allocationStatus?: AllocationStatus;
  description: ReactNode;
  linkedIssue: boolean;
  groupName: string;
  activityName: string;
  waitListProps: WaitlistButtonProps;
  mode?: RegistrationMode;
}

function AllocationButton({
  translations,
  onAllocate,
  onDeallocate,
  clash = false,
  waitListProps,
  description,
  linkedIssue = false,
  allocationStatus = "NOT_ALLOCATED",
  groupName,
  activityName,
  mode,
  ...buttonProps
}: AllocationButtonProps): React.JSX.Element {
  switch (allocationStatus) {
    case "ALLOCATED_TO_THIS": {
      return (
        <AllocatedToThis
          mode={mode}
          clash={clash}
          description={description}
          linkedIssue={linkedIssue}
          onDeallocate={onDeallocate}
          translations={translations}
          buttonProps={buttonProps}
        />
      );
    }
    case "ALLOCATED_TO_OTHER": {
      return (
        <AllocatedToOther
          waitlistProps={waitListProps}
          activityName={activityName}
          translations={translations}
          linkedIssue={linkedIssue}
          buttonProps={buttonProps}
          description={description}
          onAllocate={onAllocate}
          groupName={groupName}
          clash={clash}
        />
      );
    }
    case "NOT_ALLOCATED": {
      return (
        <NotAllocated
          waitlistProps={waitListProps}
          translations={translations}
          buttonProps={buttonProps}
          description={description}
          linkedIssue={linkedIssue}
          onAllocate={onAllocate}
          clash={clash}
          groupName={groupName}
          activityName={activityName}
        />
      );
    }
    case "MULTIPLE_ALLOCATIONS": {
      return (
        <MultipleAllocations
          translations={translations}
          buttonProps={buttonProps}
          description={description}
          linkedIssue={linkedIssue}
          onDeallocate={onDeallocate}
          clash={clash}
        />
      );
    }
    default: {
      return exhaustiveMatchingGuard(allocationStatus);
    }
  }
}
